// OrtoSoft — i18n — React provider for browser-language detection + manual switch
// French for *fr* browsers, English otherwise; the manual choice wins and is
// remembered (localStorage). No dependencies.

import { createContext, useContext, useEffect, useState } from 'react';
import fr from './fr';
import en from './en';
import ro from './ro';

export const dictionaries = { fr, en, ro };

export function detectLang(navigatorLike) {
  const lang = ((navigatorLike && navigatorLike.language) || 'en').toLowerCase();
  if (lang.startsWith('fr')) return 'fr';
  if (lang.startsWith('ro')) return 'ro';
  return 'en';
}

const STORAGE_KEY = 'ortosoft-locale';

function initialLocale() {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'fr' || saved === 'en' || saved === 'ro') return saved;
  }
  return typeof navigator !== 'undefined' ? detectLang(navigator) : 'fr';
}

const I18nContext = createContext({
  locale: 'fr',
  setLocale: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(initialLocale);

  const setLocale = (next) => {
    if (next !== 'fr' && next !== 'en' && next !== 'ro') return;
    setLocaleState(next);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, next);
    }
  };

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = (key) => {
    const dict = dictionaries[locale] || fr;
    return dict[key] !== undefined ? dict[key] : key;
  };

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
